// 2026-10-26 — «Cambios Reporte Reconocimiento Diario.docx», cambio 1: pestaña «Reconocimientos
// Recibidos» del Excel del Reco Diario.
import type { OUItem } from '@/api/types';
import { collectDescendantItems } from '@/components/common/OuTree/ouTree';

/**
 * OUs de quien recibe para la hoja «Reconocimientos Recibidos», o `undefined` cuando la hoja va sin
 * registros: sin OU elegida («Todas las OUs») o con TODAS las del alcance marcadas a mano. Con alcance
 * global lo recibido es lo mismo que lo enviado, y repetir el detalle lo duplicaría.
 *
 * La lista es el subárbol COMPLETO de cada OU elegida dentro del alcance. No pasa por
 * `resolveQueryOuIdsMulti`: ese cae a la selección sin expandir por encima de 60 OUs (tope del query
 * string), y aquí la lista viaja en el cuerpo de un POST; sin expandir, una OU raíz no casaría con la
 * OU de nadie, porque las personas cuelgan de sus descendientes.
 */
export function ousDeRecibidos(
  ousElegidas: readonly string[],
  ouItems: OUItem[],
  scopedOuItems: readonly OUItem[],
): string[] | undefined {
  if (ousElegidas.length === 0 || scopedOuItems.length === 0) return undefined;

  const expandidas = expandirEnElAlcance(ousElegidas, ouItems, scopedOuItems);
  return expandidas.size === 0 || cubreElAlcance(expandidas, scopedOuItems) ? undefined : [...expandidas];
}

/**
 * 2026-10-27 — ¿las OUs elegidas cubren TODO el alcance? «Todas las OUs» (sin filtro) daba 13
 * reconocimientos y marcar a mano las 6 OUs daba 10: con OUs el backend cuenta por la OU de quien
 * ENVÍA, y quien no tiene OU (o la tiene fuera del árbol) queda fuera. Marcar todas significa lo mismo
 * que no filtrar, así que las consultas se mandan sin OU (misma regla que RDR-2 para Recibidos).
 */
export function cubreTodoElAlcance(
  ousElegidas: readonly string[],
  ouItems: OUItem[],
  scopedOuItems: readonly OUItem[],
): boolean {
  if (ousElegidas.length === 0 || scopedOuItems.length === 0) return false;
  return cubreElAlcance(expandirEnElAlcance(ousElegidas, ouItems, scopedOuItems), scopedOuItems);
}

/** Subárbol completo de cada OU elegida, recortado al alcance. */
function expandirEnElAlcance(
  ousElegidas: readonly string[],
  ouItems: OUItem[],
  scopedOuItems: readonly OUItem[],
): Set<string> {
  const delAlcance = new Set(scopedOuItems.map((ou) => ou.id));
  const expandidas = new Set<string>();
  for (const ouId of ousElegidas) {
    for (const id of [ouId, ...collectDescendantItems(ouItems, ouId).map((ou) => ou.id)]) {
      if (delAlcance.has(id)) expandidas.add(id);
    }
  }
  return expandidas;
}

function cubreElAlcance(expandidas: ReadonlySet<string>, scopedOuItems: readonly OUItem[]): boolean {
  return scopedOuItems.every((ou) => expandidas.has(ou.id));
}
